// Files on a register item, grouped by document. Each document shows its current version; older versions stay
// downloadable. "New version" uploads a replacement (like Smartsheet's "upload new version").
import { fileTag } from '../lib/fileType';
import { useRef, useState } from 'react';
import { downloadFile, write } from '../lib/api';
import { uploadFile } from '../lib/files';
import { fmtBytes, fmtDateTime } from '../lib/format';
import { Badge, Empty, errText, useToast } from './kit';
import { canView, useViewer } from './Viewer';
import { FileNote, FileStatus, TagEditor } from './FileTags';
import { isOld, shownStatus } from '../lib/fileTags';
import type { Org } from './item/shared';

export type FileRow = {
  id: string;
  original_name: string;
  mime: string | null;
  size_bytes: number | null;
  kind: 'file' | 'link';
  external_url: string | null;
  doc_key: string;
  version: number;
  is_current: boolean;
  comment_id: string | null;
  uploaded_at: string | null;
  uploaded_by_name: string | null;
  source: string;
  from_org_id?: string | null;
  in_pack?: boolean;
  notes?: string | null;
  status_tag?: string | null;
  review_flag?: boolean | null;
  superseded_by?: string | null;
  /** V0.4.10: attached to a response line */
  response_id?: string | null;
  note_by_name?: string | null;
  note_at?: string | null;
  created_at: string;
  deleted_at: string | null;
};

const ext = (f: FileRow) => fileTag(f.original_name, f.kind).label;

export function ItemFiles({
  files,
  siteId,
  itemId,
  canEdit,
  onChanged,
  orgs = [],
}: {
  files: FileRow[];
  siteId: string;
  itemId: string;
  canEdit: boolean;
  onChanged: () => void;
  /** organisations for the "From" note on each file */
  orgs?: Org[];
}) {
  const toast = useToast();
  const addIn = useRef<HTMLInputElement>(null);
  const verIn = useRef<HTMLInputElement>(null);
  const [replacing, setReplacing] = useState<FileRow | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [open, setOpen] = useState<Record<string, boolean>>({});
  const [over, setOver] = useState(false);
  const [editing, setEditing] = useState<{ id: string; from: string } | null>(null);
  const [tagging, setTagging] = useState<string | null>(null);
  const [show, setShow] = useState<'all' | 'current' | 'review'>('all');
  const orgName = (id: string | null | undefined) => (id ? orgs.find((o) => o.id === id)?.name ?? '—' : null);

  async function saveFoot() {
    if (!editing) return;
    try {
      await write('files', 'update', { id: editing.id, values: { from_org_id: editing.from || null }, summary: 'Updated who sent the file' });
      setEditing(null);
      onChanged();
    } catch (e) {
      toast({ tone: 'error', text: errText(e) });
    }
  }

  const live = files.filter((f) => !f.deleted_at);
  const docs = new Map<string, FileRow[]>();
  for (const f of live) docs.set(f.doc_key, [...(docs.get(f.doc_key) ?? []), f]);
  const groups = [...docs.values()]
    .map((vs) => {
      const sorted = [...vs].sort((a, b) => b.version - a.version);
      const current = sorted.find((v) => v.is_current) ?? sorted[0];
      return { current, older: sorted.filter((v) => v.id !== current.id) };
    })
    .sort(
      (a, b) =>
        Number(isOld(shownStatus(a.current))) - Number(isOld(shownStatus(b.current))) ||
        String(b.current.uploaded_at ?? b.current.created_at).localeCompare(String(a.current.uploaded_at ?? a.current.created_at)),
    );
  const nCurrent = groups.filter((g) => !isOld(shownStatus(g.current, g.older.length > 0))).length;
  const nReview = groups.filter((g) => g.current.review_flag).length;
  const listed = groups.filter((g) => (show === 'current' ? !isOld(shownStatus(g.current, g.older.length > 0)) : show === 'review' ? g.current.review_flag : true));
  const allCurrent = groups.map((g) => g.current);

  const viewer = useViewer();
  const dl = (f: FileRow) => void downloadFile(f.id).catch((e) => toast({ tone: 'error', text: errText(e) }));
  const asView = (f: FileRow) => ({ id: f.id, name: f.original_name, mime: f.mime, kind: f.kind });

  async function add(list: FileList | File[]) {
    for (const f of Array.from(list)) {
      setBusy(`Uploading ${f.name}…`);
      try {
        await uploadFile(f, { siteId, linkedTable: 'items', linkedId: itemId });
      } catch (e) {
        toast({ tone: 'error', text: `${f.name}: ${errText(e)}` });
      }
    }
    setBusy(null);
    onChanged();
  }

  async function newVersion(file: File, of: FileRow) {
    setBusy(`Uploading new version of ${of.original_name}…`);
    try {
      await uploadFile(file, { siteId, linkedTable: 'items', linkedId: itemId, replaces: of.id, keepOriginal: true });
      toast({ tone: 'ok', text: `New version of ${of.original_name} uploaded` });
    } catch (e) {
      toast({ tone: 'error', text: errText(e) });
    }
    setBusy(null);
    setReplacing(null);
    onChanged();
  }

  async function removeDoc(g: { current: FileRow; older: FileRow[] }) {
    const n = 1 + g.older.length;
    if (!window.confirm(`Delete ${g.current.original_name}${n > 1 ? ` and its ${n - 1} older version(s)` : ''}? It can be restored from the audit log.`)) return;
    const batch = crypto.randomUUID();
    try {
      for (const f of [g.current, ...g.older]) await write('files', 'delete', { id: f.id, batch_id: batch, summary: `Deleted ${f.original_name} v${f.version}` });
      onChanged();
    } catch (e) {
      toast({ tone: 'error', text: errText(e) });
    }
  }

  return (
    <div>
      {canEdit ? (
        <div
          className={`drop ${over ? 'over' : ''}`}
          onDragOver={(e) => (e.preventDefault(), setOver(true))}
          onDragLeave={() => setOver(false)}
          onDrop={(e) => {
            e.preventDefault();
            setOver(false);
            if (e.dataTransfer.files.length) void add(e.dataTransfer.files);
          }}
        >
          {busy ?? (
            <>
              Drop files here or{' '}
              <button type="button" className="linkbtn" onClick={() => addIn.current?.click()}>
                choose files
              </button>
            </>
          )}
          <input ref={addIn} type="file" multiple hidden onChange={(e) => e.target.files && void add(e.target.files)} />
          <input
            ref={verIn}
            type="file"
            hidden
            onChange={(e) => {
              const f = e.target.files?.[0];
              if (f && replacing) void newVersion(f, replacing);
              e.target.value = '';
            }}
          />
        </div>
      ) : null}
      {groups.length > 1 || nReview ? (
        <div className="chips ffilter" role="group" aria-label="Show files">
          <button type="button" className={`chip ${show === 'all' ? 'on' : ''}`} aria-pressed={show === 'all'} onClick={() => setShow('all')}>
            All {groups.length}
          </button>
          <button type="button" className={`chip ${show === 'current' ? 'on' : ''}`} aria-pressed={show === 'current'} onClick={() => setShow('current')}>
            Current only {nCurrent}
          </button>
          {nReview ? (
            <button type="button" className={`chip chip-review ${show === 'review' ? 'on' : ''}`} aria-pressed={show === 'review'} onClick={() => setShow('review')}>
              To review {nReview}
            </button>
          ) : null}
        </div>
      ) : null}
      {groups.length === 0 ? (
        <Empty>No files yet.</Empty>
      ) : (
        listed.map((g) => (
          <div className={`doc ${isOld(shownStatus(g.current, g.older.length > 0)) ? 'doc-old' : ''}`} key={g.current.doc_key}>
            <span className={`doc-ico ${fileTag(g.current.original_name, g.current.kind).cls}`} aria-hidden="true">
              {ext(g.current)}
            </span>
            <div className="doc-main">
              <div className="doc-name">
                <button
                  type="button"
                  className="linkbtn"
                  title={canView(asView(g.current)) ? 'View' : g.current.kind === 'link' ? 'Open link' : 'Download'}
                  onClick={() => viewer(asView(g.current), groups.map((x) => asView(x.current)))}
                >
                  {g.current.original_name}
                </button>{' '}
                {g.current.version > 1 || g.older.length ? <Badge tone="dark">v{g.current.version}</Badge> : null}{' '}
                {g.current.comment_id ? <Badge>on a comment</Badge> : null}{' '}
                {g.current.response_id ? <Badge>on a response</Badge> : null}{' '}
                <FileStatus f={g.current} hasOlder={g.older.length > 0} others={allCurrent} onOpen={(fid) => {
                  const t = groups.find((x) => x.current.id === fid);
                  if (t) viewer(asView(t.current), groups.map((x) => asView(x.current)));
                }} />
              </div>
              <FileNote f={g.current} />
              <div className="small muted">
                {g.current.kind === 'link' ? 'Link' : fmtBytes(g.current.size_bytes)} · {g.current.uploaded_by_name ? `${g.current.uploaded_by_name} · ` : ''}
                {fmtDateTime(g.current.uploaded_at ?? g.current.created_at)}
                {g.current.source === 'smartsheet' ? ' · from Smartsheet' : ''}
              </div>
              {tagging === g.current.id ? (
                <TagEditor
                  f={g.current}
                  others={allCurrent}
                  onCancel={() => setTagging(null)}
                  onDone={() => {
                    setTagging(null);
                    onChanged();
                  }}
                />
              ) : null}
              {editing?.id === g.current.id ? (
                <div className="ffoot-edit">
                  <label>
                    From
                    <select value={editing.from} autoFocus onChange={(e) => setEditing({ ...editing, from: e.target.value })}>
                      <option value="">—</option>
                      {orgs.map((o) => (
                        <option key={o.id} value={o.id}>
                          {o.name}
                        </option>
                      ))}
                    </select>
                  </label>
                  <span className="grow" />
                  <button type="button" className="btn btn-primary btn-sm" onClick={() => void saveFoot()}>
                    Save
                  </button>
                  <button type="button" className="btn btn-secondary btn-sm" onClick={() => setEditing(null)}>
                    Cancel
                  </button>
                </div>
              ) : orgs.length ? (
                <div className="ffoot">
                  <span className="ffoot-part">
                    {canEdit ? (
                      <button type="button" className="qe" onClick={() => setEditing({ id: g.current.id, from: g.current.from_org_id ?? '' })} aria-label="Edit who the file came from">
                        <span className="muted">From:</span> {orgName(g.current.from_org_id) ? <span className="strong">{orgName(g.current.from_org_id)}</span> : <em className="muted">who sent it?</em>}
                      </button>
                    ) : (
                      <>
                        <span className="muted">From:</span> {orgName(g.current.from_org_id) ?? '—'}
                      </>
                    )}
                  </span>
                </div>
              ) : null}
              <div className="cmt-actions">
                {g.current.kind === 'file' ? (
                  <button type="button" className="linkbtn" onClick={() => dl(g.current)}>
                    Download
                  </button>
                ) : null}
                {canEdit ? (
                  <button type="button" className="linkbtn strong" aria-label={`Tag and note for ${g.current.original_name}`} onClick={() => setTagging(tagging === g.current.id ? null : g.current.id)}>
                    Tag · note
                  </button>
                ) : null}
                {g.older.length ? (
                  <button type="button" className="linkbtn" onClick={() => setOpen((o) => ({ ...o, [g.current.doc_key]: !o[g.current.doc_key] }))}>
                    {open[g.current.doc_key] ? 'Hide' : 'Show'} {g.older.length} older version{g.older.length > 1 ? 's' : ''}
                  </button>
                ) : null}
                {canEdit && g.current.kind === 'file' ? (
                  <button
                    type="button"
                    className="linkbtn"
                    onClick={() => {
                      setReplacing(g.current);
                      verIn.current?.click();
                    }}
                  >
                    Upload new version
                  </button>
                ) : null}
                {canEdit ? (
                  <button type="button" className="linkbtn danger" onClick={() => void removeDoc(g)}>
                    Delete
                  </button>
                ) : null}
              </div>
              {open[g.current.doc_key] ? (
                <ul className="versions">
                  {g.older.map((v) => (
                    <li key={v.id}>
                      <Badge>v{v.version}</Badge>
                      <FileStatus f={v} />
                      <button type="button" className="linkbtn" onClick={() => viewer(asView(v), [asView(g.current), ...g.older.map(asView)])}>
                        {v.original_name}
                      </button>
                      <button type="button" className="linkbtn" onClick={() => dl(v)}>
                        Download
                      </button>
                      <span className="muted">
                        {fmtBytes(v.size_bytes)} · {v.uploaded_by_name ? `${v.uploaded_by_name} · ` : ''}
                        {fmtDateTime(v.uploaded_at ?? v.created_at)}
                      </span>
                    </li>
                  ))}
                </ul>
              ) : null}
            </div>
          </div>
        ))
      )}
    </div>
  );
}
